// Recovery for "Importing a module script failed" — the error 16 parents hit
// between Aug 17-19 (CHILDRENS-HOUR-3 / -6).
//
// Cause: every deploy publishes new content-hashed chunks and drops the old
// ones. A tab that loaded build A still asks for build A's chunk names, so the
// first lazy route a user opens after a deploy 404s and the dynamic import
// rejects. WebKit is where it shows up, because iOS keeps tabs alive for days;
// the same tab on Android is usually discarded and reloaded long before.
//
// The shell is the stale part, so a reload fixes it: index.html is served
// no-cache and the service worker fetches navigations network-first
// (src/sw.ts), so the next load gets current chunk names.
//
// Deliberately NOT calling preventDefault() on vite:preloadError. Letting the
// error propagate keeps Sentry recording it, which is how we'll know whether
// this fix actually worked; the reload lands fast enough that the error
// boundary is at most a flash.

const RELOAD_COUNT_KEY = "stale-chunk-reloads";

// A reload only helps when the chunk is genuinely stale. If it 404s because
// the deploy itself is broken, reloading forever would lock every user out of
// the site, so give up after this many tries and let the error boundary show.
const MAX_RELOADS = 2;

// Once the app has run this long without a stale-chunk failure, the reload
// budget resets — otherwise a long-lived tab that recovered twice days ago
// would refuse to recover from today's deploy.
const COUNTER_RESET_MS = 30 * 1000;

// Each engine words it differently; all three mean the same failed import.
const STALE_CHUNK_MESSAGES = [
  /importing a module script failed/i, // WebKit / Safari
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
];

function messageOf(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  return "";
}

export function isStaleChunkError(value: unknown): boolean {
  const message = messageOf(value);
  return message !== "" && STALE_CHUNK_MESSAGES.some((re) => re.test(message));
}

// sessionStorage throws in Safari private browsing — never let bookkeeping be
// the reason recovery doesn't happen.
function readAttempts(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0") || 0;
  } catch {
    return 0;
  }
}

function writeAttempts(value: number): void {
  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(value));
  } catch {
    /* private mode — proceed without a budget rather than not at all */
  }
}

let reloading = false;

/** Reloads once into the current build. Returns false if the budget is spent. */
export function reloadForStaleChunk(): boolean {
  if (reloading) return true;
  const attempts = readAttempts();
  if (attempts >= MAX_RELOADS) return false;
  writeAttempts(attempts + 1);
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Wraps a lazy-import factory so a failed chunk fetch retries once (covers a
 * blip on the network) and, if it is a stale-build chunk, reloads into the
 * current build instead of leaving a blank screen behind the error boundary.
 */
export function retryDynamicImport<T>(factory: () => Promise<T>): () => Promise<T> {
  return () =>
    factory().catch((error) => {
      if (!isStaleChunkError(error)) throw error;
      return new Promise<T>((resolve, reject) => {
        window.setTimeout(() => {
          factory().then(resolve, (retryError) => {
            if (isStaleChunkError(retryError) && reloadForStaleChunk()) {
              // Reload is underway; never settle so nothing renders an error.
              return;
            }
            reject(retryError);
          });
        }, 400);
      });
    });
}

export function initStaleChunkReload(): void {
  // Vite's preload helper wraps every lazy import and dispatches this when the
  // chunk (or one of its preloaded deps) fails to load.
  window.addEventListener("vite:preloadError", () => {
    reloadForStaleChunk();
  });

  // Safety net for imports that reject outside the preload helper. Both paths
  // were represented in Sentry: CHILDRENS-HOUR-3 arrived through the error
  // boundary, CHILDRENS-HOUR-6 unhandled via react-dom.
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) reloadForStaleChunk();
  });

  window.addEventListener("error", (event) => {
    if (isStaleChunkError(event.error ?? event.message)) reloadForStaleChunk();
  });

  // Survived startup — this build's chunks are reachable, so restore the budget.
  window.setTimeout(() => {
    if (reloading) return;
    try {
      sessionStorage.removeItem(RELOAD_COUNT_KEY);
    } catch {
      /* nothing to clear */
    }
  }, COUNTER_RESET_MS);
}
